import React, { useRef, useEffect, useState } from 'react';
import * as d3 from 'd3';

const MARGIN = { top: 20, right: 20, bottom: 60, left: 60 };
const CHART_HEIGHT = 350;

const styles = {
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  header: {
    marginBottom: '16px',
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1f2937',
    marginBottom: '2px',
  },
  subtitle: {
    fontSize: '12px',
    color: '#9ca3af',
    marginBottom: '8px',
  },
  legend: {
    display: 'flex',
    gap: '16px',
    flexWrap: 'wrap',
    marginBottom: '4px',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '11px',
    color: '#6b7280',
  },
  container: {
    overflowX: 'auto',
  },
  tooltip: {
    position: 'fixed',
    pointerEvents: 'none',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '8px 12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    fontSize: '12px',
    zIndex: 1000,
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
};

export default function BoxPlotTimeline({ data }) {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);

  useEffect(() => {
    if (!data || data.length === 0) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const containerWidth = containerRef.current
      ? containerRef.current.clientWidth
      : 600;
    const width = Math.max(600, containerWidth);
    const innerW = width - MARGIN.left - MARGIN.right;
    const innerH = CHART_HEIGHT - MARGIN.top - MARGIN.bottom;

    svg.attr('width', width).attr('height', CHART_HEIGHT);

    const g = svg.append('g').attr('transform', `translate(${MARGIN.left}, ${MARGIN.top})`);

    // Scales
    const x = d3.scaleBand()
      .domain(data.map((d) => d.date))
      .range([0, innerW])
      .padding(0.2);

    const allValues = data.flatMap((d) => {
      const vals = [d.min, d.max];
      if (d.outliers && d.outliers.length > 0) {
        vals.push(...d.outliers);
      }
      return vals;
    });
    const priceMin = d3.min(allValues);
    const priceMax = d3.max(allValues);
    const pricePadding = (priceMax - priceMin) * 0.1 || 1;

    const y = d3.scaleLinear()
      .domain([priceMin - pricePadding, priceMax + pricePadding])
      .nice()
      .range([innerH, 0]);

    // Horizontal grid lines
    const yTicks = y.ticks();
    g.selectAll('.grid-line')
      .data(yTicks)
      .enter()
      .append('line')
      .attr('class', 'grid-line')
      .attr('x1', 0)
      .attr('x2', innerW)
      .attr('y1', (d) => y(d))
      .attr('y2', (d) => y(d))
      .attr('stroke', '#f3f4f6')
      .attr('stroke-width', 1);

    // X axis
    g.append('g')
      .attr('transform', `translate(0, ${innerH})`)
      .call(d3.axisBottom(x).tickSize(0).tickFormat((dateStr) => {
        const match = data.find((d) => d.date === dateStr);
        return match ? match.label : dateStr;
      }))
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280')
      .attr('transform', 'rotate(-30)')
      .attr('text-anchor', 'end')
      .attr('dx', '-0.5em')
      .attr('dy', '0.5em');

    g.select('.domain').attr('stroke', '#e5e7eb');

    // Y axis
    g.append('g')
      .call(d3.axisLeft(y).ticks(6).tickFormat((v) => `$${v}`))
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280');

    g.selectAll('.domain').attr('stroke', '#e5e7eb');
    g.selectAll('.tick line').attr('stroke', '#e5e7eb');

    // Box plots
    const boxWidth = x.bandwidth() * 0.6;
    const capWidth = boxWidth * 0.4;

    data.forEach((d) => {
      const cx = x(d.date) + x.bandwidth() / 2;
      const iqr = d.q3 - d.q1;
      const whiskerLow = Math.max(d.min, d.q1 - 1.5 * iqr);
      const whiskerHigh = Math.min(d.max, d.q3 + 1.5 * iqr);

      // Lower whisker (vertical line from whiskerLow to Q1)
      g.append('line')
        .attr('x1', cx)
        .attr('x2', cx)
        .attr('y1', y(whiskerLow))
        .attr('y2', y(d.q1))
        .attr('stroke', '#6b7280')
        .attr('stroke-width', 1);

      // Upper whisker (vertical line from Q3 to whiskerHigh)
      g.append('line')
        .attr('x1', cx)
        .attr('x2', cx)
        .attr('y1', y(d.q3))
        .attr('y2', y(whiskerHigh))
        .attr('stroke', '#6b7280')
        .attr('stroke-width', 1);

      // Lower cap
      g.append('line')
        .attr('x1', cx - capWidth / 2)
        .attr('x2', cx + capWidth / 2)
        .attr('y1', y(whiskerLow))
        .attr('y2', y(whiskerLow))
        .attr('stroke', '#6b7280')
        .attr('stroke-width', 1);

      // Upper cap
      g.append('line')
        .attr('x1', cx - capWidth / 2)
        .attr('x2', cx + capWidth / 2)
        .attr('y1', y(whiskerHigh))
        .attr('y2', y(whiskerHigh))
        .attr('stroke', '#6b7280')
        .attr('stroke-width', 1);

      // Box (Q1 to Q3)
      g.append('rect')
        .attr('x', cx - boxWidth / 2)
        .attr('y', y(d.q3))
        .attr('width', boxWidth)
        .attr('height', Math.max(0, y(d.q1) - y(d.q3)))
        .attr('fill', '#dbeafe')
        .attr('stroke', '#2563eb')
        .attr('stroke-width', 1.5)
        .attr('rx', 2);

      // Median line
      g.append('line')
        .attr('x1', cx - boxWidth / 2)
        .attr('x2', cx + boxWidth / 2)
        .attr('y1', y(d.median))
        .attr('y2', y(d.median))
        .attr('stroke', '#1d4ed8')
        .attr('stroke-width', 2);

      // Mean diamond (rotated square)
      const diamondSize = 4;
      const meanY = y(d.mean);
      g.append('polygon')
        .attr('points', [
          [cx, meanY - diamondSize],
          [cx + diamondSize, meanY],
          [cx, meanY + diamondSize],
          [cx - diamondSize, meanY],
        ].map((p) => p.join(',')).join(' '))
        .attr('fill', '#f59e0b');

      // Outliers
      if (d.outliers && d.outliers.length > 0) {
        d.outliers.forEach((ov) => {
          g.append('circle')
            .attr('cx', cx)
            .attr('cy', y(ov))
            .attr('r', 3)
            .attr('fill', '#dc2626')
            .attr('stroke', '#fff')
            .attr('stroke-width', 1);
        });
      }

      // Invisible hover target covering the full whisker extent plus outliers
      const hoverTop = d.outliers && d.outliers.length > 0
        ? Math.min(whiskerHigh, ...d.outliers.filter((v) => v > whiskerHigh))
        : whiskerHigh;
      const hoverBottom = d.outliers && d.outliers.length > 0
        ? Math.max(whiskerLow, ...d.outliers.filter((v) => v < whiskerLow))
        : whiskerLow;
      const hoverYMax = Math.max(hoverTop, whiskerHigh, ...(d.outliers || []));
      const hoverYMin = Math.min(hoverBottom, whiskerLow, ...(d.outliers || []));

      g.append('rect')
        .attr('x', cx - boxWidth / 2 - 4)
        .attr('y', y(hoverYMax) - 4)
        .attr('width', boxWidth + 8)
        .attr('height', Math.max(8, y(hoverYMin) - y(hoverYMax) + 8))
        .attr('fill', 'transparent')
        .style('cursor', 'pointer')
        .on('mousemove', (event) => {
          setTooltip({
            x: event.clientX + 12,
            y: event.clientY - 10,
            date: d.date,
            label: d.label,
            count: d.count,
            min: d.min,
            q1: d.q1,
            median: d.median,
            mean: d.mean,
            q3: d.q3,
            max: d.max,
            outlierCount: d.outliers ? d.outliers.length : 0,
          });
        })
        .on('mouseleave', () => {
          setTooltip(null);
        });
    });

    return () => {
      svg.selectAll('*').remove();
    };
  }, [data]);

  if (!data || data.length === 0) {
    return (
      <div style={styles.card}>
        <div style={styles.title}>Price Distribution Timeline</div>
        <div style={{ color: '#6b7280', textAlign: 'center', padding: '40px 0' }}>
          No distribution data available
        </div>
      </div>
    );
  }

  return (
    <div style={styles.card}>
      <div style={styles.header}>
        <div style={styles.title}>Price Distribution Timeline</div>
        <div style={styles.subtitle}>
          Box-and-whisker plots showing pest365 spread per scrape date
        </div>
        <div style={styles.legend}>
          <div style={styles.legendItem}>
            <div style={{ width: 12, height: 10, background: '#dbeafe', border: '1px solid #2563eb', borderRadius: 1 }} />
            <span>IQR (Q1-Q3)</span>
          </div>
          <div style={styles.legendItem}>
            <div style={{ width: 12, height: 2, background: '#1d4ed8' }} />
            <span>Median</span>
          </div>
          <div style={styles.legendItem}>
            <div style={{ width: 8, height: 8, background: '#f59e0b', transform: 'rotate(45deg)' }} />
            <span>Mean</span>
          </div>
          <div style={styles.legendItem}>
            <div style={{ width: 8, height: 8, background: '#dc2626', borderRadius: '50%' }} />
            <span>Outlier</span>
          </div>
        </div>
      </div>
      <div style={styles.container} ref={containerRef}>
        <svg ref={svgRef} />
      </div>
      {tooltip && (
        <div
          style={{
            ...styles.tooltip,
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          <div style={{ fontWeight: 600, color: '#1f2937', marginBottom: '4px' }}>
            {tooltip.label} ({tooltip.date})
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: '#6b7280' }}>Count</span>
            <span style={{ fontWeight: 600 }}>{tooltip.count}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: '#6b7280' }}>Min</span>
            <span style={{ fontWeight: 600 }}>${tooltip.min.toFixed(2)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: '#6b7280' }}>Q1</span>
            <span style={{ fontWeight: 600 }}>${tooltip.q1.toFixed(2)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: '#1d4ed8', fontWeight: 500 }}>Median</span>
            <span style={{ fontWeight: 600, color: '#1d4ed8' }}>${tooltip.median.toFixed(2)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: '#f59e0b', fontWeight: 500 }}>Mean</span>
            <span style={{ fontWeight: 600, color: '#f59e0b' }}>${tooltip.mean.toFixed(2)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: '#6b7280' }}>Q3</span>
            <span style={{ fontWeight: 600 }}>${tooltip.q3.toFixed(2)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: '#6b7280' }}>Max</span>
            <span style={{ fontWeight: 600 }}>${tooltip.max.toFixed(2)}</span>
          </div>
          {tooltip.outlierCount > 0 && (
            <div style={{ borderTop: '1px solid #e5e7eb', marginTop: '4px', paddingTop: '4px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
                <span style={{ color: '#dc2626' }}>Outliers</span>
                <span style={{ fontWeight: 600, color: '#dc2626' }}>{tooltip.outlierCount}</span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
